import { ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Bottone "Acquista ora": aggiunge la variante selezionata (con quantità,
 * proprietà e piano di abbonamento del form prodotto) al carrello e porta
 * direttamente al checkout. Aggiorna il prezzo al cambio di variante e quantità.
 */
class BuyNowComponent extends HTMLElement {
  #abortController = new AbortController();

  connectedCallback() {
    const { signal } = this.#abortController;

    this.button?.addEventListener('click', this.#onClick, { signal });

    const scope = this.closest('.shopify-section, dialog, product-card');
    scope?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });

    const form = this.form;
    form?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#updatePrice, { signal });
    form?.addEventListener('input', this.#onFormInput, { signal });
    form?.addEventListener('change', this.#onFormInput, { signal });

    // Ripristina il bottone se l'utente torna indietro dal checkout (bfcache)
    window.addEventListener('pageshow', () => this.#setLoading(false), { signal });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#abortController = new AbortController();
  }

  /** @returns {HTMLButtonElement | null} */
  get button() {
    return this.querySelector('.buy-now-button');
  }

  /** @returns {HTMLFormElement | null} */
  get form() {
    const byId = this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
    if (byId instanceof HTMLFormElement && byId.contains(this)) return byId;
    return this.closest('form');
  }

  get quantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.form?.querySelector('input[name="quantity"]'));
    const value = parseInt(input?.value ?? '1', 10);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  /** @param {Event} event */
  #onFormInput = (event) => {
    if (event.target instanceof HTMLInputElement && event.target.name === 'quantity') {
      this.#updatePrice();
    }
  };

  #updatePrice = () => {
    const priceEl = this.querySelector('[data-buy-now-price]');
    const compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-buy-now-compare]'));
    const unit = parseInt(this.dataset.unitPrice ?? '', 10);
    const unitCompare = parseInt(this.dataset.unitComparePrice ?? '', 10);
    const { moneyFormat, currency } = this.dataset;
    if (!priceEl || !Number.isFinite(unit) || !moneyFormat || !currency) return;

    const qty = this.quantity;
    try {
      priceEl.textContent = formatMoney(unit * qty, moneyFormat, currency);

      if (compareEl) {
        const onSale = Number.isFinite(unitCompare) && unitCompare > unit;
        compareEl.hidden = !onSale;
        compareEl.textContent = onSale ? formatMoney(unitCompare * qty, moneyFormat, currency) : '';
      }
    } catch (error) {
      // In caso di formato non riconosciuto lasciamo il prezzo renderizzato dal server
      console.warn('[buy-now] formattazione prezzo non riuscita', error);
    }
  };

  /** @param {any} event - VariantUpdateEvent */
  #onVariantUpdate = (event) => {
    const { data, resource } = event.detail ?? {};
    if (!data) return;

    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && String(data.productId) !== this.dataset.productId) {
      return;
    }

    /** @type {HTMLElement | null} */
    const fresh =
      data.html?.querySelector(`buy-now-component[data-block-id="${this.dataset.blockId}"]`) ??
      data.html?.querySelector('buy-now-component');

    if (fresh) {
      if (fresh.dataset.unitPrice) this.dataset.unitPrice = fresh.dataset.unitPrice;
      this.dataset.unitComparePrice = fresh.dataset.unitComparePrice ?? '';
      if (fresh.dataset.moneyFormat) this.dataset.moneyFormat = fresh.dataset.moneyFormat;
      if (fresh.dataset.currency) this.dataset.currency = fresh.dataset.currency;

      const freshPrice = fresh.querySelector('[data-buy-now-price]');
      const priceEl = this.querySelector('[data-buy-now-price]');
      if (freshPrice && priceEl) priceEl.textContent = freshPrice.textContent;
    }

    const available = resource != null && resource.available !== false;
    if (this.button) this.button.disabled = !available;
    if (this.dataset.hideUnavailable === 'true') this.hidden = !available;
    this.#hideError();
    this.#updatePrice();
  };

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    event.preventDefault();
    const button = this.button;
    const form = this.form;
    if (!button || !form || button.disabled || button.getAttribute('aria-busy') === 'true') return;

    // Rispetta i campi obbligatori del form (es. destinatario carta regalo)
    if (!form.reportValidity()) return;

    this.#hideError();
    this.#setLoading(true);

    try {
      const formData = new FormData(form);
      if (!formData.get('quantity')) formData.set('quantity', String(this.quantity));

      // `Theme` è dichiarato con const in snippets/scripts.liquid (scope globale, non su window)
      // @ts-ignore
      const themeRoutes = typeof Theme !== 'undefined' ? Theme.routes : undefined;
      // @ts-ignore
      const addUrl = themeRoutes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });

      if (!response.ok) {
        let message = '';
        try {
          const body = await response.json();
          message = body.description || body.message || '';
        } catch (_) {}
        throw new Error(message);
      }

      window.location.href = this.dataset.checkoutUrl || '/checkout';
    } catch (error) {
      this.#setLoading(false);
      const message = error instanceof Error && error.message ? error.message : this.dataset.errorText;
      this.#showError(message || '');
    }
  };

  /** @param {boolean} loading */
  #setLoading(loading) {
    const button = this.button;
    if (!button) return;
    if (loading) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
  }

  /** @param {string} message */
  #showError(message) {
    const el = this.querySelector('.buy-now-block__error');
    if (!el || !message) return;
    el.textContent = message;
    el.hidden = false;
  }

  #hideError() {
    const el = this.querySelector('.buy-now-block__error');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }
}

if (!customElements.get('buy-now-component')) {
  customElements.define('buy-now-component', BuyNowComponent);
}
